// One session-wide notification stream; the header and open inbox ask their own reads.
import { createContext, type ReactNode, useCallback, useContext, useRef } from "react";
import { showing } from "../lib/read";
import { useReadEvents } from "../lib/read-events";
import { refresh, useRead } from "../lib/reads";
import { NOTIFICATION_EVENTS_URI, notifications } from "../reads/notifications";

const COUNT_QUERY = { limit: 1, read_state: "unread" } as const;
type Subscribe = (listener: () => void) => () => void;
export const NotificationUpdatesContext = createContext<Subscribe | null>(null);
const Unread = createContext<number | null>(null);

export function NotificationUpdates({ children }: { readonly children: ReactNode }) {
  // Count comes from the original caller projection. At most one item accompanies it;
  // no history is downloaded and no caller/tenant identity comes from the browser.
  const held = useRead(notifications.page, { query: COUNT_QUERY });
  const listeners = useRef(new Set<() => void>());
  const subscribe = useCallback<Subscribe>((listener) => {
    listeners.current.add(listener);
    return () => {
      listeners.current.delete(listener);
    };
  }, []);
  const refreshNotices = useCallback(() => {
    refresh(notifications.page, { query: COUNT_QUERY });
    for (const listener of listeners.current) listener();
  }, []);
  useReadEvents(
    NOTIFICATION_EVENTS_URI,
    held.status === "loaded" || held.status === "refreshing",
    refreshNotices,
  );
  return (
    <NotificationUpdatesContext value={subscribe}>
      <Unread value={showing(held)?.unread_count ?? null}>{children}</Unread>
    </NotificationUpdatesContext>
  );
}

export function useUnreadNotifications() {
  return useContext(Unread);
}
